import {
	currentTargetRequired,
	getAttributeOrThrow,
	PUB_SUB_EVENTS,
	qsaRequired,
	removeTrapFocus,
	trapFocus,
} from '@/scripts/core/global'
import { subscribe, type SubscriberCallback } from '@/scripts/core/global'
import { qsRequired, targetRequired } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class QuantityInput extends UcoastEl {
	static htmlSelector = 'quantity-input'
	input: HTMLInputElement
	changeEvent: Event
	quantityUpdateUnsubscriber: SubscriberCallback | undefined = undefined
	toggle: HTMLButtonElement
	panel: HTMLElement
	optionsContainer: HTMLElement
	options: NodeListOf<HTMLButtonElement>
	closeEl: HTMLButtonElement
	scrollContainer: HTMLElement
	boundOnKeyUpEscape: (event: KeyboardEvent) => void
	boundOnOptionSelect: (event: Event) => void
	boundOnOverlayClick: (event: Event) => void
	valueTextEl: HTMLElement
	constructor() {
		super()
		this.input = qsRequired('input', this)
		this.toggle = qsRequired('button', this)
		this.scrollContainer = qsRequired('cart-drawer-items')
		this.panel = qsRequired('[data-cart-quantity-panel]')
		this.optionsContainer = qsRequired('[data-cart-quantity-options]')
		this.options = qsaRequired<HTMLButtonElement>(
			'[data-cart-quantity-option]'
		)
		this.closeEl = qsRequired<HTMLButtonElement>(
			'[data-cart-quantity-close]'
		)
		this.valueTextEl = qsRequired('[data-cart-quantity-value]', this)
		this.changeEvent = new Event('change', { bubbles: true })
		this.input.addEventListener('change', this.onInputChange.bind(this))
		this.toggle.addEventListener('click', this.onSummaryClick.bind(this))
		this.boundOnKeyUpEscape = this.onKeyUpEscape.bind(this)
		this.boundOnOptionSelect = this.onOptionSelect.bind(this)
		this.boundOnOverlayClick = this.onOverlayClick.bind(this)
	}

	onSummaryClick(event: Event) {
		event.preventDefault()
		return this.isOpen() ? this.close() : this.open()
	}

	onOverlayClick(event: Event) {
		event.preventDefault()
		this.close()
	}

	getPanelPosition() {
		const toggleRect = this.toggle.getBoundingClientRect()
		const togglePositionFromTop = toggleRect.top + toggleRect.height
		return togglePositionFromTop
	}

	getOptionToFocus() {
		const index = parseInt(this.input.value) - 1
		const option = this.options[index]
		if (option) return option
		return this.options[0]
	}

	open() {
		this.toggle.setAttribute('aria-expanded', 'true')
		this.optionsContainer.style.top = `${this.getPanelPosition()}px`
		this.optionsContainer.classList.add('active')
		this.panel.classList.add('active')
		window.setTimeout(() => {
			this.panel.classList.add('active')
		}, 1)

		trapFocus(this.panel, this.getOptionToFocus())
		this.panel.addEventListener('keyup', this.boundOnKeyUpEscape)
		this.closeEl.addEventListener('click', this.boundOnOverlayClick)
		this.options.forEach((option) => {
			option.addEventListener('click', this.boundOnOptionSelect)
		})
	}
	onOptionSelect(e: Event) {
		e.preventDefault()
		e.stopPropagation()
		const option = currentTargetRequired(e)
		const optionValue = getAttributeOrThrow('data-value', option)
		this.input.value = optionValue
		this.valueTextEl.textContent = optionValue
		this.input.dispatchEvent(this.changeEvent)
		this.close()
	}
	close() {
		this.options.forEach((option) => {
			option.removeEventListener('click', this.boundOnOptionSelect)
		})
		this.closeEl.removeEventListener('click', this.boundOnOverlayClick)
		this.optionsContainer.classList.remove('active')

		this.toggle.setAttribute('aria-expanded', 'false')
		removeTrapFocus(this.input)
		window.setTimeout(() => {
			this.panel.classList.remove('active')
		}, 301)
	}

	isOpen() {
		return this.toggle.getAttribute('aria-expanded') === 'true'
	}

	onKeyUpEscape(event: KeyboardEvent) {
		if (event.code.toUpperCase() !== 'ESCAPE') return
		if (!this.isOpen()) return
		event.stopPropagation()
		this.panel.removeEventListener('keyup', this.boundOnKeyUpEscape)
		this.close()
	}

	override connectedCallback() {
		this.validateQtyRules()
		this.quantityUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.quantityUpdate,
			this.validateQtyRules.bind(this)
		)
	}

	override disconnectedCallback() {
		if (this.quantityUpdateUnsubscriber) {
			this.quantityUpdateUnsubscriber(undefined)
		}
	}

	onInputChange(_event: Event) {
		this.validateQtyRules()
	}

	onButtonClick(event: Event) {
		event.preventDefault()
		const previousValue = this.input.value
		const target = targetRequired<Event, HTMLButtonElement>(event)

		target.name === 'plus' ? this.input.stepUp() : this.input.stepDown()
		if (previousValue !== this.input.value)
			this.input.dispatchEvent(this.changeEvent)
	}

	validateQtyRules() {
		const value = parseInt(this.input.value)
		if (this.input.min) {
			const min = parseInt(this.input.min)
			const buttonMinus = qsRequired(
				".quantity__button[name='minus']",
				this
			)
			buttonMinus.classList.toggle('disabled', value <= min)
		}
		if (this.input.max) {
			const max = parseInt(this.input.max)
			const buttonPlus = qsRequired(
				".quantity__button[name='plus']",
				this
			)
			buttonPlus.classList.toggle('disabled', value >= max)
		}
	}
}
